import { WifiOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsOnline } from '@/hooks/useIsOnline';

// Global offline indicator (offline-resilience substrate). Renders nothing when online, so it is safe
// to mount anywhere — but per the shell mount point it is only mounted inside the mobile MobileShell,
// never in the desktop marketing <Layout>. On web (desktop) navigator.onLine is virtually always true,
// so this stays invisible there regardless.
//
// Accessible: role="status" + aria-live="polite" announces the state change without stealing focus.

interface OfflineBannerProps {
  /** Extra classes (e.g. to position it relative to a particular shell). */
  className?: string;
}

export function OfflineBanner({ className }: OfflineBannerProps) {
  const online = useIsOnline();

  if (online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        // Fixed strip just below the safe-area inset, above the bottom tab bar (z-50). Sits at z-[60]
        // so it overlays page content but stays under modal/version-gate chrome (z-[100]).
        'fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-2',
        'px-3 py-1.5 pt-[calc(0.375rem+env(safe-area-inset-top))]',
        'bg-amber-500/95 text-[12px] font-medium text-amber-950 shadow-sm backdrop-blur-sm',
        className,
      )}
    >
      <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>You&apos;re offline — showing saved data</span>
    </div>
  );
}

export default OfflineBanner;
